import {
  buildA4Pdf,
  imageFileToPage,
  isImageFile,
  isPdfFile,
  MAX_DOCUMENT_BYTES,
  readPdfPages,
  type PageImage,
} from '../capture/document';

/** The note's own files (#915 C1): at most 16 live, each one PDF up to 25 MB, a label up to 120. */
export const MAX_NOTE_FILES = 16;
export const MAX_LABEL = 120;

/** A file chosen in the form, waiting for the save to send it. */
export type PendingFile = {
  readonly key: string;
  readonly name: string;
  readonly label: string;
  readonly pdf: Blob;
  readonly source: 'pdf' | 'images';
  readonly pages: number;
};

export type RefusedReason = 'type' | 'size' | 'unreadable' | 'limit';
export type RefusedFile = {
  readonly key: string;
  readonly name: string;
  readonly reason: RefusedReason;
  /** The file's size in MB, for «È di 31 MB». */
  readonly sizeMb: number;
};

export type Prepared = { readonly accepted: PendingFile[]; readonly refused: RefusedFile[] };

export type PrepareDeps = {
  readonly readPages: (pdf: Blob) => Promise<number>;
  readonly toPage: (file: File) => Promise<PageImage>;
  readonly build: (pages: readonly PageImage[]) => Promise<Blob>;
};

const DEVICE: PrepareDeps = {
  readPages: (pdf) => readPdfPages(pdf),
  toPage: imageFileToPage,
  build: (pages) => buildA4Pdf(pages),
};

let counter = 0;
const nextKey = (): string => `note-file-${String(++counter)}`;
const mb = (bytes: number): number => Math.ceil(bytes / (1024 * 1024));

/**
 * The chosen files as the note will hold them: each PDF on its own, the photos joined into one PDF
 * on the device as for an expense. A refused file says why; the others go ahead. `room` is how
 * many more files the note may take.
 */
export async function prepareFiles(
  files: readonly File[],
  room: number,
  photosName: (pages: number) => string,
  deps: PrepareDeps = DEVICE,
): Promise<Prepared> {
  const accepted: PendingFile[] = [];
  const refused: RefusedFile[] = [];
  const refuse = (name: string, reason: RefusedReason, bytes: number): void => {
    refused.push({ key: nextKey(), name, reason, sizeMb: mb(bytes) });
  };
  const take = (file: Omit<PendingFile, 'key' | 'label'>): void => {
    if (accepted.length >= room) refuse(file.name, 'limit', file.pdf.size);
    else accepted.push({ ...file, key: nextKey(), label: '' });
  };
  const photos = files.filter((file) => !isPdfFile(file) && isImageFile(file));
  for (const file of files) {
    if (isPdfFile(file)) {
      await preparePdf(file, deps, take, refuse);
    } else if (!isImageFile(file)) {
      refuse(file.name, 'type', file.size);
    }
  }
  if (photos.length > 0) await preparePhotos(photos, deps, photosName, take, refuse);
  return { accepted, refused };
}

async function preparePdf(
  file: File,
  deps: PrepareDeps,
  take: (file: Omit<PendingFile, 'key' | 'label'>) => void,
  refuse: (name: string, reason: RefusedReason, bytes: number) => void,
): Promise<void> {
  if (file.size > MAX_DOCUMENT_BYTES) {
    refuse(file.name, 'size', file.size);
    return;
  }
  try {
    const pages = await deps.readPages(file);
    take({ name: file.name, pdf: file, source: 'pdf', pages });
  } catch {
    refuse(file.name, 'unreadable', file.size);
  }
}

async function preparePhotos(
  photos: readonly File[],
  deps: PrepareDeps,
  photosName: (pages: number) => string,
  take: (file: Omit<PendingFile, 'key' | 'label'>) => void,
  refuse: (name: string, reason: RefusedReason, bytes: number) => void,
): Promise<void> {
  const name = photosName(photos.length);
  try {
    const pages = await Promise.all(photos.map((photo) => deps.toPage(photo)));
    const pdf = await deps.build(pages);
    if (pdf.size > MAX_DOCUMENT_BYTES) refuse(name, 'size', pdf.size);
    else take({ name, pdf, source: 'images', pages: pages.length });
  } catch {
    refuse(name, 'unreadable', 0);
  }
}

/** «1 pagina · 180 KB», «3 pagine · 2,4 MB». */
export function sizeText(bytes: number): { readonly amount: string; readonly unit: 'KB' | 'MB' } {
  if (bytes < 1024 * 1024)
    return { amount: String(Math.max(1, Math.round(bytes / 1024))), unit: 'KB' };
  return { amount: (bytes / (1024 * 1024)).toFixed(1).replace('.', ','), unit: 'MB' };
}
